<template>
  <CPopover
    class="c-entity-selector"
    :overlap="overlap"
    placement="top-start"
    :showArrow="false"
    to="body"
    trigger="click"
    @update:show="handlePopoverVisibilityChange"
  >
    <template v-for="(_, name) in $slots" #[name]="slotProps">
      <slot :name="name" v-bind="slotProps || {}"></slot>
    </template>
    <CCard :bordered="false" :style="{ minWidth: `${width}px` }">
      <div class="flex flex-col gap-1">
        <CCard :bordered="false">
          <div class="font-weight-medium" :style="{ color: themeVars.text }">
            {{
              multiple && selectedCount
                ? t('canvasUI.components.entitySelector.selectedCount', { count: selectedCount })
                : headerTitle
            }}
          </div>
        </CCard>
        <CDivider class="ma-0 px-2" />
        <CSearch
          v-if="searchFn && data?.length >= searchThreshold"
          ref="searchRef"
          v-model="search"
          autofocus
          hide-border
          :loading="isFetching"
        />
        <RecycleScroller
          v-slot="{ item }: { item: Item }"
          :buffer="itemSize * maxItemsToRender * 2"
          :itemSize="itemSize"
          :items="items"
          keyField="id"
          :style="data.length > maxItemsToRender ? { height: `${itemSize * maxItemsToRender}px` } : undefined"
        >
          <div
            :key="item.id"
            class="c-entity-selector__recycle-list-item px-4 text-caption font-weight-medium"
            :class="{
              'c-entity-selector__recycle-list-item--active': selectedItemIds?.includes(item.id),
              'cursor-pointer': !multiple && !disabledMap[item.id],
            }"
            :disabled="!!disabledMap[item.id]"
            :style="{
              background: isListItemHoveredId === item.id ? themeVars.primary6 : themeVars.base,
            }"
            @click="onListItemClick(item)"
            @mouseleave="isListItemHoveredId = null"
            @mouseover="isListItemHoveredId = item.id"
          >
            <div class="flex items-center gap-2">
              <CCheckbox
                v-if="multiple"
                :checked="selectedItemIds?.includes(item.id)"
                @update:checked="(val) => handleCheckboxChange(val, item)"
              />
              <div>
                <div class="block truncate">
                  {{ renderProp('title', item) }}
                </div>

                <div
                  v-if="subtitle"
                  class="block truncate c-entity-selector__recycle-list-item--subtitle"
                  :style="{ color: themeVars.text2 }"
                >
                  {{ renderProp('subtitle', item) }}
                </div>
              </div>
            </div>
          </div>
        </RecycleScroller>
      </div>
    </CCard>
  </CPopover>
</template>

<script setup lang="ts">
import CCard from '@lib/components/CCard/CCard.vue'
import CCheckbox from '@lib/components/CCheckbox/CCheckbox.vue'
import CDivider from '@lib/components/CDivider/CDivider.vue'
import CPopover from '@lib/components/CPopover/CPopover.vue'
import CSearch from '@lib/components/CSearch/CSearch.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import { computed, nextTick, ref, type ComponentPublicInstance, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { RecycleScroller } from 'vue-virtual-scroller'

interface Props {
  query: (params?: any) => { data: Ref; isFetching: Ref<boolean> }
  searchFn?: (search: string, item: unknown) => boolean
  filterFn?: (item: unknown) => boolean
  sortFn?: (items: unknown, selectedList: unknown) => unknown
  searchThreshold?: number
  disabledMap?: Record<string, boolean>
  itemSize?: number
  maxItemsToRender?: number
  headerTitle: string
  title: ((item: any) => string) | string
  subtitle?: ((item: any) => string) | string
  width?: number
  multiple?: boolean
  selectedItemIds?: any[]
  overlap?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  searchThreshold: 4,
  disabledMap: () => ({}),
  itemSize: 56,
  maxItemsToRender: 4,
  headerTitle: '',
  width: 250,
  multiple: false,
  selectedItemIds: () => [],
  subtitle: '',
  overlap: false,
})

const emit = defineEmits<{
  (e: 'select', item: any): void
  (e: 'update:selectedItemIds', newSelectedIds: any[]): void
}>()

defineSlots()

const { t } = useI18n()
const themeVars = useThemeVars()
const search = ref('')
const searchRef: Ref<ComponentPublicInstance | null> = ref(null)

interface Item {
  id: number | string
  [key: string]: any
}
const isListItemHoveredId: Ref<Item['id'] | null> = ref(null)
const { data, isFetching } = props.query()
const selectedCount = computed(() => props.selectedItemIds?.length ?? 0)

const handlePopoverVisibilityChange = (isVisible: boolean) => {
  if (!isVisible) {
    search.value = ''
  }
}

const items = computed(() => {
  const res = (data.value ?? [])
    .filter((item: unknown) => {
      if (!props.searchFn || data.value?.length < props.searchThreshold) return true

      return props.searchFn(search.value, item)
    })
    .filter((item: unknown) => {
      if (!props.filterFn) return true

      return props.filterFn(item)
    })

  return props.sortFn ? props.sortFn(res, props.selectedItemIds) : res
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const renderProp = (prop: 'subtitle' | 'title', item: any) => {
  const pValue = props[prop]

  if (typeof item !== 'object' || item === null)
    throw new Error('[CEntitySelector]: Invalid type or null entry in list')
  else if (typeof pValue === 'function') return pValue(item)
  else if (typeof pValue === 'string' && pValue in item) return item[pValue]

  return pValue
}

const handleCheckboxChange = (isChecked: boolean, item: any) => {
  let newSelectedIds = props.selectedItemIds || []
  if (!isChecked) {
    newSelectedIds = newSelectedIds.filter((id) => id !== item.id)
  } else if (!newSelectedIds.includes(item.id)) {
    newSelectedIds = [...newSelectedIds, item.id]
  }

  emit('update:selectedItemIds', newSelectedIds)
}

const onListItemClick = (item: unknown) => {
  emit('select', item)
  nextTick(() => {
    searchRef.value?.$el.getElementsByTagName('input')?.[0]?.focus() // keep search focus
  })
}
</script>
